// src/pages/UserProfile.js (dokončení)
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Nav, Tab, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../api/services/user';
import { ConfirmModal } from '../components/UI/Modal';

const UserProfile = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const queryClient = useQueryClient();


    // Fetch user profile - opraveno pro React Query v5
    const { data: profile, isLoading, error } = useQuery({
        queryKey: ['userProfile'],
        queryFn: () => userService.getProfile().then(res => res.data)
    });

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: ''
    });

    // Update profile mutation
    const updateProfileMutation = useMutation({
        mutationFn: (updatedProfile) => userService.updateProfile(updatedProfile),
        onSuccess: () => {
            setIsEditing(false);
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
        }
    });

    // Set form data when profile is loaded
    useEffect(() => {
        if (profile) {
            setFormData({
                firstName: profile.firstName || '',
                lastName: profile.lastName || '',
                email: profile.email || '',
                company: profile.company || '',
                phone: profile.phone || ''
            });
        }
    }, [profile]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateProfileMutation.mutate(formData);
    };

    const handleDeleteAccount = () => {
        // Implementace smazání účtu
        setShowDeleteModal(false);
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání profilu...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">Došlo k chybě při načítání profilu.</Alert>;
    }

    return (
        <Container fluid className="">
            <h1 className="mb-4 ">Můj profil</h1>

            <Tab.Container defaultActiveKey="profile">
                <Row>
                    <Col lg={3} md={4} className="mb-4">
                        <Card className="shadow-sm">
                            <Card.Body className="p-0">
                                <div className="text-center p-4 bg-light">
                                    <div className="avatar-circle mb-3 mx-auto">
                                        {profile.firstName && profile.lastName ? (
                                            <span className="avatar-initials">
                        {profile.firstName.charAt(0)}{profile.lastName.charAt(0)}
                      </span>
                                        ) : (
                                            <i className="fas fa-user"></i>
                                        )}
                                    </div>
                                    <h5 className="mb-1">{profile.firstName} {profile.lastName}</h5>
                                    <p className="text-muted small mb-0">Členem od: {new Date(profile.createdAt).toLocaleDateString()}</p>
                                </div>

                                <Nav variant="pills" className="flex-column">
                                    <Nav.Item>
                                        <Nav.Link eventKey="profile">
                                            <i className="fas fa-user me-2"></i> Osobní údaje
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="security">
                                            <i className="fas fa-lock me-2"></i> Zabezpečení
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="subscription">
                                            <i className="fas fa-credit-card me-2"></i> Předplatné
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="danger" className="text-danger">
                                            <i className="fas fa-exclamation-triangle me-2"></i> Nebezpečná zóna
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={9} md={8}>
                        <Card className="shadow-sm">
                            <Card.Body>
                                <Tab.Content>
                                    <Tab.Pane eventKey="profile">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h4 className="mb-0">Osobní údaje</h4>
                                            {!isEditing && (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => setIsEditing(true)}
                                                >
                                                    <i className="fas fa-pencil-alt me-2"></i> Upravit
                                                </Button>
                                            )}
                                        </div>

                                        {updateProfileMutation.isError && (
                                            <Alert variant="danger">
                                                Při ukládání došlo k chybě. Zkuste to prosím znovu.
                                            </Alert>
                                        )}

                                        {updateProfileMutation.isSuccess && (
                                            <Alert variant="success">
                                                Změny byly úspěšně uloženy.
                                            </Alert>
                                        )}

                                        {!isEditing ? (
                                            <div>
                                                <Row className="mb-2">
                                                    <Col md={3} className="text-secondary">Email:</Col>
                                                    <Col md={9}>{profile.email}</Col>
                                                </Row>

                                                <Row className="mb-2">
                                                    <Col md={3} className="text-secondary">Jméno:</Col>
                                                    <Col md={9}>{profile.firstName} {profile.lastName}</Col>
                                                </Row>

                                                {profile.company && (
                                                    <Row className="mb-2">
                                                        <Col md={3} className="text-secondary">Společnost:</Col>
                                                        <Col md={9}>{profile.company}</Col>
                                                    </Row>
                                                )}

                                                {profile.phone && (
                                                    <Row className="mb-2">
                                                        <Col md={3} className="text-secondary">Telefon:</Col>
                                                        <Col md={9}>{profile.phone}</Col>
                                                    </Row>
                                                )}

                                                <Row className="mb-2">
                                                    <Col md={3} className="text-secondary">Typ účtu:</Col>
                                                    <Col md={9}>
                                                        {profile.subscription ? (
                                                            <span className="badge bg-primary">{profile.subscription.plan}</span>
                                                        ) : (
                                                            <span className="badge bg-secondary">Základní</span>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </div>
                                        ) : (
                                            <Form onSubmit={handleSubmit}>
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Jméno</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="firstName"
                                                                value={formData.firstName}
                                                                onChange={handleChange}
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Příjmení</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="lastName"
                                                                value={formData.lastName}
                                                                onChange={handleChange}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Form.Group className="mb-3">
                                                    <Form.Label>Email</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        disabled
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Email nelze změnit.
                                                    </Form.Text>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label>Společnost (volitelné)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="company"
                                                        value={formData.company}
                                                        onChange={handleChange}
                                                    />
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label>Telefon (volitelné)</Form.Label>
                                                    <Form.Control
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                    />
                                                </Form.Group>

                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => setIsEditing(false)}
                                                    >
                                                        Zrušit
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        variant="primary"
                                                        disabled={updateProfileMutation.isLoading}
                                                    >
                                                        {updateProfileMutation.isLoading ? (
                                                            <>
                                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                                Ukládání...
                                                            </>
                                                        ) : 'Uložit změny'}
                                                    </Button>
                                                </div>
                                            </Form>
                                        )}
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="security">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h4 className="mb-0">Zabezpečení účtu</h4>
                                        </div>

                                        <Card className="mb-3 shadow-sm">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="d-flex align-items-center mb-2">
                                                            <i className="fas fa-key fa-lg text-primary me-3"></i>
                                                            <h5 className="mb-0">Změna hesla</h5>
                                                        </div>
                                                        <p className="text-muted mb-0">
                                                            Pravidelná změna hesla zvyšuje bezpečnost vašeho účtu
                                                        </p>
                                                    </div>
                                                    <Button variant="primary">
                                                        <i className="fas fa-lock me-2"></i> Změnit heslo
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        <Card className="shadow-sm">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="d-flex align-items-center mb-2">
                                                            <i className="fas fa-shield-alt fa-lg text-primary me-3"></i>
                                                            <h5 className="mb-0">Dvoufaktorové ověření</h5>
                                                        </div>
                                                        <p className="text-muted mb-0">
                                                            Přidejte další úroveň zabezpečení pomocí druhého ověřovacího faktoru
                                                        </p>
                                                    </div>
                                                    <Button variant="outline-primary">
                                                        <i className="fas fa-plus me-2"></i> Nastavit 2FA
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="subscription">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h4 className="mb-0">Předplatné a fakturace</h4>
                                        </div>

                                        <Card className="mb-4 shadow-sm">
                                            <Card.Body>
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="me-4">
                                                        <i className="fas fa-crown fa-2x text-warning"></i>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h5 className="mb-1">Aktuální plán</h5>
                                                        <p className="mb-0 fs-4 fw-bold">
                                                            {profile.subscription ? profile.subscription.plan : 'Základní'}
                                                            {profile.subscription && (
                                                                <span className="ms-2 fs-6 text-muted fw-normal">
                                  Platnost do: {new Date(profile.subscription.validUntil).toLocaleDateString()}
                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <Button variant="primary">
                                                            <i className="fas fa-arrow-up me-2"></i> Vylepšit plán
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        <Row>
                                            <Col md={6} className="mb-4">
                                                <Card className="shadow-sm h-100">
                                                    <Card.Body className="text-center p-4">
                                                        <div className="mb-3">
                                                            <div className="avatar-circle bg-primary mx-auto">
                                                                <i className="fas fa-file-invoice fa-lg text-white"></i>
                                                            </div>
                                                        </div>
                                                        <h5>Fakturační údaje</h5>
                                                        <p className="text-muted">Spravujte vaše fakturační údaje pro platby</p>
                                                        <Button variant="outline-primary">
                                                            <i className="fas fa-edit me-2"></i> Upravit údaje
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-4">
                                                <Card className="shadow-sm h-100">
                                                    <Card.Body className="text-center p-4">
                                                        <div className="mb-3">
                                                            <div className="avatar-circle bg-primary mx-auto">
                                                                <i className="fas fa-history fa-lg text-white"></i>
                                                            </div>
                                                        </div>
                                                        <h5>Historie plateb</h5>
                                                        <p className="text-muted">Zobrazit historii vašich plateb a faktury</p>
                                                        <Button variant="outline-primary">
                                                            <i className="fas fa-list me-2"></i> Zobrazit historii
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="danger">
                                        <div className="mb-4">
                                            <h4 className="text-danger mb-3">Nebezpečná zóna</h4>
                                            <Alert variant="danger">
                                                <div className="d-flex">
                                                    <div className="me-3">
                                                        <i className="fas fa-exclamation-triangle fa-2x"></i>
                                                    </div>
                                                    <div>
                                                        <h5>Pozor!</h5>
                                                        <p className="mb-0">
                                                            Akce v této sekci jsou nevratné. Postupujte opatrně.
                                                        </p>
                                                    </div>
                                                </div>
                                            </Alert>
                                        </div>

                                        <Card className="border-danger shadow-sm">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="d-flex align-items-center mb-2">
                                                            <i className="fas fa-user-slash fa-lg text-danger me-3"></i>
                                                            <h5 className="text-danger mb-0">Smazat účet</h5>
                                                        </div>
                                                        <p>
                                                            Smazáním účtu přijdete o všechna data a nastavení. Tato akce je nevratná.
                                                        </p>
                                                    </div>
                                                    <Button
                                                        variant="outline-danger"
                                                        onClick={() => setShowDeleteModal(true)}
                                                    >
                                                        <i className="fas fa-trash-alt me-2"></i> Smazat účet
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Tab.Pane>
                                </Tab.Content>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Tab.Container>

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteAccount}
                title="Potvrzení smazání účtu"
                message="Opravdu chcete smazat svůj účet? Tato akce je nevratná a všechna vaše data a nastavení budou odstraněna. Monitorování všech webů bude ukončeno."
                confirmText="Ano, smazat účet"
                cancelText="Zrušit"
                variant="danger"
            />
        </Container>
    );
};

export default UserProfile;